/**
 * @boli/core - Bidding Service
 *
 * Platform-agnostic bidding logic for Boli Live.
 * This service is used by BOTH Web (Phase 1) and Mobile (Phase 2) with ZERO changes.
 *
 * Key Features:
 * - Idempotency key generation for duplicate prevention
 * - Time synchronization for accurate countdowns
 * - Anti-sniping (15-second extension logic)
 * - Optimistic updates with rollback
 * - Rate limiting (10 bids/minute)
 */

import type {
  Auction,
  Bid,
  BidRequest,
  BidResponse,
  BidRejectionReason,
  ApiResponse,
  RealtimeEvent,
  BidPlacedEvent,
} from '@boli/types';
import { IdempotencyService } from '../utils/idempotency';
import { TimeSyncService } from '../utils/time-sync';

// ============================================================================
// INTERFACES
// ============================================================================

export interface IBiddingService {
  /**
   * Submit a bid for an auction
   * @param auctionId - The auction ID
   * @param amount - The bid amount in PKR
   * @param userId - The authenticated user's ID (required for idempotency)
   */
  placeBid(auctionId: string, amount: number, userId: string): Promise<BidResponse>;

  /**
   * Get current auction state
   */
  getAuction(auctionId: string): Promise<Auction | null>;

  /**
   * Get bid history for an auction
   */
  getBids(auctionId: string, limit?: number): Promise<Bid[]>;

  /**
   * Subscribe to real-time bid updates
   */
  subscribeToBids(
    auctionId: string,
    callback: (event: RealtimeEvent<BidPlacedEvent>) => void
  ): () => void;

  /**
   * Check if user can bid (rate limiting, KYC, etc.)
   */
  canUserBid(userId: string, auctionId: string, amount: number): Promise<BidEligibility>;

  /**
   * Get time remaining until auction ends
   * @param endTimestamp - The auction end timestamp in milliseconds
   */
  getTimeRemaining(endTimestamp: number): number;

  /**
   * Calculate minimum next bid amount
   */
  getMinimumBid(currentHighBid: number): number;
}

export interface BidEligibility {
  canBid: boolean;
  reason?: BidRejectionReason;
  cooldownMs?: number; // If rate limited
  kycRequired?: boolean;
  paymentRequired?: boolean;
}

export interface BiddingServiceConfig {
  apiBaseUrl: string;
  maxBidsPerMinute: number;
  minBidIncrement: number; // Percentage (e.g., 0.05 = 5%)
  antiSnipingWindowMs: number; // Last N seconds trigger extension
  extensionDurationMs: number; // How long to extend
  maxExtensions: number; // Maximum number of extensions
}

// ============================================================================
// DEFAULT CONFIG
// ============================================================================

export const DEFAULT_BIDDING_CONFIG: BiddingServiceConfig = {
  apiBaseUrl: '',
  maxBidsPerMinute: 10,
  minBidIncrement: 0.05, // 5% minimum increment
  antiSnipingWindowMs: 10000, // Last 10 seconds
  extensionDurationMs: 15000, // 15 second extension
  maxExtensions: 20, // Max 5 minutes total extension
};

// ============================================================================
// BIDDING SERVICE IMPLEMENTATION
// ============================================================================

export class BiddingService implements IBiddingService {
  private config: BiddingServiceConfig;
  private idempotency: IdempotencyService;
  private timeSync: TimeSyncService;
  private bidHistory: Map<string, number[]> = new Map(); // userId -> timestamps
  private subscriptions: Map<string, Set<(event: RealtimeEvent<BidPlacedEvent>) => void>> =
    new Map();

  // Abstract methods - to be implemented by platform-specific adapters
  protected fetchAdapter: (url: string, options?: RequestInit) => Promise<Response>;
  protected realtimeAdapter?: {
    subscribe: (channel: string, callback: (data: unknown) => void) => () => void;
  };

  constructor(
    config: Partial<BiddingServiceConfig>,
    fetchAdapter: (url: string, options?: RequestInit) => Promise<Response>,
    realtimeAdapter?: {
      subscribe: (channel: string, callback: (data: unknown) => void) => () => void;
    }
  ) {
    this.config = { ...DEFAULT_BIDDING_CONFIG, ...config };
    this.fetchAdapter = fetchAdapter;
    this.realtimeAdapter = realtimeAdapter;
    this.idempotency = new IdempotencyService();
    this.timeSync = new TimeSyncService();
  }

  /**
   * Initialize the bidding service
   */
  async initialize(timeSyncEndpoint: string): Promise<void> {
    await this.timeSync.initialize(timeSyncEndpoint, this.fetchAdapter);
  }

  /**
   * Submit a bid for an auction
   * @param auctionId - The auction ID
   * @param amount - The bid amount in PKR
   * @param userId - The authenticated user's ID (required for unique idempotency key)
   */
  async placeBid(auctionId: string, amount: number, userId: string): Promise<BidResponse> {
    if (!userId) {
      return {
        success: false,
        error: {
          code: 'UNAUTHORIZED',
          message: 'User must be authenticated to place a bid',
        },
      };
    }

    // Generate idempotency key using actual userId for uniqueness
    const idempotencyKey = this.idempotency.generateBidKey(
      auctionId,
      userId,
      amount
    );

    // Get current server time
    const clientTimestamp = this.timeSync.getServerTime();

    const request: BidRequest = {
      auctionId,
      amount,
      idempotencyKey,
      clientTimestamp,
    };

    try {
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/auctions/${auctionId}/bids`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Idempotency-Key': idempotencyKey,
          },
          body: JSON.stringify(request),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: {
            code: data.code || 'UNKNOWN_ERROR',
            message: data.message || 'Failed to place bid',
          },
        };
      }

      // Mark idempotency key as used
      this.idempotency.markAsUsed(idempotencyKey);

      return {
        success: true,
        bid: data.bid,
        auction: data.auction,
      };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
        },
      };
    }
  }

  /**
   * Get current auction state
   */
  async getAuction(auctionId: string): Promise<Auction | null> {
    try {
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/auctions/${auctionId}`
      );

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch auction');
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to get auction:', error);
      return null;
    }
  }

  /**
   * Get bid history for an auction
   */
  async getBids(auctionId: string, limit: number = 50): Promise<Bid[]> {
    try {
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/auctions/${auctionId}/bids?limit=${limit}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch bids');
      }

      const data = await response.json();
      return data.bids || [];
    } catch (error) {
      console.error('Failed to get bids:', error);
      return [];
    }
  }

  /**
   * Subscribe to real-time bid updates
   */
  subscribeToBids(
    auctionId: string,
    callback: (event: RealtimeEvent<BidPlacedEvent>) => void
  ): () => void {
    if (!this.realtimeAdapter) {
      console.warn('Realtime adapter not configured');
      return () => {};
    }

    // Track subscription
    if (!this.subscriptions.has(auctionId)) {
      this.subscriptions.set(auctionId, new Set());
    }
    this.subscriptions.get(auctionId)!.add(callback);

    // Subscribe via adapter
    const unsubscribe = this.realtimeAdapter.subscribe(
      `auctions/${auctionId}/bids`,
      (data) => {
        const event = data as RealtimeEvent<BidPlacedEvent>;
        callback(event);
      }
    );

    // Return cleanup function
    return () => {
      this.subscriptions.get(auctionId)?.delete(callback);
      unsubscribe();
    };
  }

  /**
   * Check if user can bid (rate limiting, KYC, etc.)
   */
  async canUserBid(
    userId: string,
    auctionId: string,
    amount: number
  ): Promise<BidEligibility> {
    // Check rate limiting
    const now = Date.now();
    const userBids = this.bidHistory.get(userId) || [];
    const recentBids = userBids.filter((ts) => now - ts < 60000);

    if (recentBids.length >= this.config.maxBidsPerMinute) {
      const oldestBid = Math.min(...recentBids);
      const cooldownMs = 60000 - (now - oldestBid);

      return {
        canBid: false,
        reason: 'RATE_LIMITED',
        cooldownMs,
      };
    }

    // Additional checks would be done server-side
    // This is just client-side pre-validation
    return {
      canBid: true,
    };
  }

  /**
   * Record a bid attempt for rate limiting
   */
  recordBidAttempt(userId: string): void {
    const now = Date.now();
    const userBids = this.bidHistory.get(userId) || [];

    // Clean old entries
    const recentBids = userBids.filter((ts) => now - ts < 60000);
    recentBids.push(now);

    this.bidHistory.set(userId, recentBids);
  }

  /**
   * Get time remaining until auction ends
   */
  getTimeRemaining(endTimestamp: number): number {
    return this.timeSync.getTimeRemaining(endTimestamp);
  }

  /**
   * Format time remaining as MM:SS
   */
  formatTimeRemaining(milliseconds: number): string {
    return this.timeSync.formatTimeRemaining(milliseconds);
  }

  /**
   * Calculate minimum next bid amount
   */
  getMinimumBid(currentHighBid: number): number {
    const increment = Math.max(
      100, // Minimum 100 PKR increment
      Math.floor(currentHighBid * this.config.minBidIncrement)
    );
    return currentHighBid + increment;
  }

  /**
   * Get suggested bid increments
   */
  getSuggestedIncrements(currentHighBid: number): number[] {
    const base = this.getMinimumBid(currentHighBid) - currentHighBid;
    return [
      base, // Minimum increment
      base * 2, // 2x minimum
      base * 5, // 5x minimum
      base * 10, // 10x minimum
    ];
  }

  /**
   * Check if bid is in anti-sniping window
   */
  isInAntiSnipingWindow(endTimestamp: number): boolean {
    const remaining = this.getTimeRemaining(endTimestamp);
    return remaining > 0 && remaining <= this.config.antiSnipingWindowMs;
  }

  /**
   * Calculate new end time after extension
   */
  calculateExtendedEndTime(
    currentEndTimestamp: number,
    extensionCount: number
  ): number | null {
    if (extensionCount >= this.config.maxExtensions) {
      return null; // No more extensions allowed
    }
    return currentEndTimestamp + this.config.extensionDurationMs;
  }

  /**
   * Get current server time
   */
  getServerTime(): number {
    return this.timeSync.getServerTime();
  }

  /**
   * Get time sync offset
   */
  getTimeOffset(): number {
    return this.timeSync.getOffset();
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    this.timeSync.destroy();
    this.subscriptions.clear();
    this.bidHistory.clear();
    this.idempotency.clear();
  }
}

// ============================================================================
// BIDDING SERVICE FACTORY
// ============================================================================

let biddingServiceInstance: BiddingService | null = null;

export function createBiddingService(
  config: Partial<BiddingServiceConfig>,
  fetchAdapter: (url: string, options?: RequestInit) => Promise<Response>,
  realtimeAdapter?: {
    subscribe: (channel: string, callback: (data: unknown) => void) => () => void;
  }
): BiddingService {
  biddingServiceInstance = new BiddingService(config, fetchAdapter, realtimeAdapter);
  return biddingServiceInstance;
}

export function getBiddingService(): BiddingService {
  if (!biddingServiceInstance) {
    throw new Error('BiddingService not initialized. Call createBiddingService first.');
  }
  return biddingServiceInstance;
}
